import { useCallback, useEffect, useState } from "react";
import API_BASE_URL from "./apiBase";
import { TOKEN_KEY } from "./auth";

// The original projects, in the order the forms have always listed them.
// Used as a fallback so forms keep working if the project API is unreachable.
export const BUILT_IN_PROJECTS = [
  "Saan Verdante",
  "Inframantra HO",
  "Vatika Seven Elements",
  "Sovereign Park",
].map((name) => ({ name, builtIn: true }));

// The login token (every user has one; admin-only calls check its role)
export const ADMIN_TOKEN_KEY = TOKEN_KEY;

// Fired after a project is added so every mounted list (sidebar, forms,
// charts, Projects view) refreshes without a page reload.
const PROJECTS_CHANGED = "projects:changed";

// Project list from the backend (built-in + admin-added).
export function useProjects() {
  const [projects, setProjects] = useState(BUILT_IN_PROJECTS);

  const reload = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/project`);
      const body = await response.json();
      if (response.ok && Array.isArray(body.data) && body.data.length > 0) {
        setProjects(body.data);
      }
    } catch (error) {
      // Keep the built-in fallback; the forms stay usable.
      console.error("Could not load projects:", error);
    }
  }, []);

  useEffect(() => {
    reload();
    window.addEventListener(PROJECTS_CHANGED, reload);
    return () => window.removeEventListener(PROJECTS_CHANGED, reload);
  }, [reload]);

  return { projects, reload };
}

// Admin only. The backend also re-tags existing visitors/partners.
export async function renameProject(oldName, newName) {
  const response = await fetch(`${API_BASE_URL}/api/v1/project`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem(ADMIN_TOKEN_KEY) || ""}`,
    },
    body: JSON.stringify({ oldName, newName }),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(body.message || "Could not rename the project");
  }
  window.dispatchEvent(new Event(PROJECTS_CHANGED));
  return body;
}

export async function createProject(name) {
  const response = await fetch(`${API_BASE_URL}/api/v1/project`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem(ADMIN_TOKEN_KEY) || ""}`,
    },
    body: JSON.stringify({ name }),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(body.message || "Could not add the project");
  }
  window.dispatchEvent(new Event(PROJECTS_CHANGED));
  return body;
}
